import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BOOKING } from "@/constants/testIds";
import { toast } from "sonner";
import SOSButton from "@/components/SOSButton";
import CheckInOut from "@/components/CheckInOut";
import LiveGpsCard from "@/components/LiveGpsCard";
import SessionNotesForm from "@/components/SessionNotesForm";
import {
  CalendarClock, DollarSign, User, Check, X, CreditCard, Star,
  Loader2, MessageSquare, Repeat, CalendarDays, Download, Ban
} from "lucide-react";

const STATUS_CLASS = {
  requested: "bg-ochre-100 text-ochre-800",
  accepted: "bg-sage-100 text-sage-900",
  declined: "bg-stone2-200 text-stone2-800",
  paid: "bg-sage-600 text-white",
  completed: "bg-sage-50 text-sage-800 border border-sage-200",
  cancelled: "bg-stone2-100 text-stone2-500",
};

function BookingReviewForm({ bookingId, sitterId, onDone }) {
  const [rating, setRating] = useState(5);
  const [prof, setProf] = useState(5);
  const [comm, setComm] = useState(5);
  const [engage, setEngage] = useState(5);
  const [rel, setRel] = useState(5);
  const [beh, setBeh] = useState(5);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/reviews", {
        booking_id: bookingId, rating, comment,
        professionalism: prof, communication: comm,
        child_engagement: engage, reliability: rel,
        behavioral_knowledge: beh,
      });
      toast.success("Thanks for the review!");
      onDone();
    } catch (err) { toast.error(extractError(err)); }
    finally { setSaving(false); }
  };

  const CategoryRating = ({ label, value, onChange, testKey }) => (
    <div className="flex items-center justify-between">
      <span className="text-xs text-stone2-700">{label}</span>
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <button type="button" key={n} onClick={() => onChange(n)}
            data-testid={`booking-${testKey}-${bookingId}-${n}`}>
            <Star className={`w-4 h-4 ${n <= value ? "text-ochre-500 fill-ochre-500" : "text-stone2-300"}`} />
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <form onSubmit={submit} className="mt-3 grid gap-2 rounded-lg bg-stone2-50 p-3" data-testid={`booking-review-form-${bookingId}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-sage-900">Overall</span>
        <div className="flex gap-0.5">
          {[1, 2, 3, 4, 5].map((n) => (
            <button type="button" key={n} onClick={() => setRating(n)}
              data-testid={`booking-rating-${bookingId}-${n}`}>
              <Star className={`w-5 h-5 ${n <= rating ? "text-ochre-500 fill-ochre-500" : "text-stone2-300"}`} />
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-1.5 pt-2 border-t border-stone2-200">
        <CategoryRating label="Professionalism" value={prof} onChange={setProf} testKey="prof" />
        <CategoryRating label="Communication" value={comm} onChange={setComm} testKey="comm" />
        <CategoryRating label="Child engagement" value={engage} onChange={setEngage} testKey="engage" />
        <CategoryRating label="Reliability" value={rel} onChange={setRel} testKey="rel" />
        <CategoryRating label="Behavioral knowledge" value={beh} onChange={setBeh} testKey="beh" />
      </div>
      <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
        placeholder="How was your experience?"
        data-testid={`booking-review-comment-${bookingId}`}
        className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
      <button type="submit" disabled={saving} className="btn-primary text-xs justify-center"
        data-testid={`booking-review-submit-${bookingId}`}>
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3" />} Submit review
      </button>
    </form>
  );
}

export default function BookingsList() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReview, setShowReview] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/bookings"); setItems(data.results); }
    catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const respond = async (id, action) => {
    try {
      await api.post(`/bookings/${id}/respond`, { action });
      toast.success(`Booking ${action}ed`);
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const complete = async (id) => {
    try {
      await api.post(`/bookings/${id}/complete`);
      toast.success("Marked complete");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this booking?")) return;
    try {
      const { data } = await api.post(`/bookings/${id}/cancel`);
      if (data.waitlist_promoted > 0) {
        toast.success(`Cancelled — ${data.waitlist_promoted} waitlisted family notified.`);
      } else {
        toast.success("Booking cancelled");
      }
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const payNow = async (id) => {
    try {
      const { data } = await api.post("/payments/checkout", { booking_id: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(extractError(e)); }
  };

  const downloadIcal = (id) => {
    // Use fetch to include auth cookies then trigger a download blob
    fetch(`${api.defaults.baseURL}/bookings/${id}/ical`, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `booking-${id.slice(0, 8)}.ics`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      });
  };

  const downloadCalendarIcal = () => {
    fetch(`${api.defaults.baseURL}/calendar/bookings.ics`, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "autismbabysitters-calendar.ics";
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <div className="min-h-screen bg-stone2-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="section-eyebrow">Your bookings</span>
            <h1 className="mt-2 text-3xl font-heading font-bold text-sage-900">Bookings</h1>
          </div>
          <button type="button" onClick={downloadCalendarIcal} className="btn-outline-sage text-sm"
            data-testid="bookings-download-calendar">
            <Download className="w-4 h-4" /> Sync to calendar (.ics)
          </button>
        </div>

        {loading && <div className="mt-6 text-stone2-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}

        {!loading && items.length === 0 && (
          <div className="mt-6 paper-card p-10 text-center">
            <p className="text-stone2-600">No bookings yet.</p>
            {user?.role === "family" && (
              <button type="button" onClick={() => navigate("/search")} className="btn-primary mt-4">Find a sitter</button>
            )}
          </div>
        )}

        <ul className="mt-6 space-y-4" data-testid={BOOKING.list}>
          {items.map((b) => {
            const isFamily = user?.role === "family";
            return (
              <li key={b.id} className="paper-card p-5" data-testid={BOOKING.row(b.id)}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-sm text-sage-800">
                      <User className="w-4 h-4" />
                      {isFamily ? <>Sitter <span className="font-semibold text-sage-900">{b.sitter_name || "—"}</span></>
                                : <>Family <Link to={`/family/${b.family_user_id}/view`}
                                      data-testid={`booking-open-family-${b.id}`}
                                      className="font-semibold text-sage-900 hover:text-sage-600 hover:underline">
                                      {b.family_name || "—"}
                                    </Link></>}
                    </div>
                    <div className="mt-1 text-sm text-stone2-600 flex flex-wrap items-center gap-2">
                      <CalendarClock className="w-3.5 h-3.5" />
                      {new Date(b.start_at).toLocaleString()} → {new Date(b.end_at).toLocaleString()}
                      {b.priority && (
                        <span className="chip-ochre" data-testid={`booking-priority-${b.id}`}>Priority</span>
                      )}
                      {b.is_multi_day && (
                        <span className="chip-ochre"><CalendarDays className="w-3 h-3 mr-1" /> Multi-day</span>
                      )}
                      {b.series_id && (
                        <span className="chip">
                          <Repeat className="w-3 h-3 mr-1" />
                          {b.occurrence_index}/{b.occurrence_total} · {b.recurrence_pattern}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-stone2-600">
                      {b.hours}h × ${b.hourly_rate}/hr + 10% fee =
                      <span className="font-heading font-bold text-sage-900 ml-1"><DollarSign className="w-3 h-3 inline" />{b.total?.toFixed(2)}</span>
                    </div>
                    {b.notes && <div className="mt-2 text-sm text-stone2-500 italic">"{b.notes}"</div>}
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${STATUS_CLASS[b.status] || "bg-stone2-100"}`}
                      data-testid={`booking-status-${b.id}`}>
                      {b.status}
                    </span>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {!isFamily && b.status === "requested" && (
                    <>
                      <button type="button" onClick={() => respond(b.id, "accept")} data-testid={BOOKING.accept(b.id)}
                        className="btn-primary text-xs"><Check className="w-3.5 h-3.5" /> Accept</button>
                      <button type="button" onClick={() => respond(b.id, "decline")} data-testid={BOOKING.decline(b.id)}
                        className="btn-secondary text-xs"><X className="w-3.5 h-3.5" /> Decline</button>
                    </>
                  )}
                  {isFamily && ["accepted", "requested"].includes(b.status) && (
                    <button type="button" onClick={() => payNow(b.id)} data-testid={BOOKING.pay(b.id)}
                      className="btn-accent text-xs"><CreditCard className="w-3.5 h-3.5" /> Pay now</button>
                  )}
                  {(b.status === "paid") && (
                    <button type="button" onClick={() => complete(b.id)} data-testid={BOOKING.complete(b.id)}
                      className="btn-outline-sage text-xs"><Check className="w-3.5 h-3.5" /> Mark complete</button>
                  )}
                  {isFamily && b.status === "completed" && (
                    <button type="button" onClick={() => setShowReview(showReview === b.id ? null : b.id)} data-testid={BOOKING.review(b.id)}
                      className="btn-outline-sage text-xs"><Star className="w-3.5 h-3.5" /> Leave review</button>
                  )}
                  <button type="button"
                    onClick={() => navigate(`/messages?to=${isFamily ? b.sitter_user_id : b.family_user_id}`)}
                    className="btn-secondary text-xs"><MessageSquare className="w-3.5 h-3.5" /> Message</button>
                  <button type="button" onClick={() => downloadIcal(b.id)}
                    data-testid={`booking-ical-${b.id}`}
                    className="btn-secondary text-xs"><Download className="w-3.5 h-3.5" /> .ics</button>
                  {!["completed", "cancelled", "declined"].includes(b.status) && (
                    <button type="button" onClick={() => cancel(b.id)}
                      data-testid={`booking-cancel-${b.id}`}
                      className="text-xs font-semibold rounded-full px-3 py-1.5 text-danger border border-danger/40 hover:bg-danger/5 transition-colors duration-200">
                      <Ban className="w-3.5 h-3.5 inline mr-1" /> Cancel
                    </button>
                  )}
                  {["accepted", "paid"].includes(b.status) && (
                    <SOSButton bookingId={b.id} size="sm" testId={`booking-sos-${b.id}`} />
                  )}
                  {["accepted", "paid"].includes(b.status) && (
                    <CheckInOut booking={b} myRole={user?.role} onChange={load} />
                  )}
                </div>
                {["accepted", "paid"].includes(b.status) && (
                  <LiveGpsCard booking={b} myRole={user?.role} />
                )}
                {!isFamily && ["accepted","paid","completed"].includes(b.status) && (
                  <div className="mt-2">
                    <SessionNotesForm bookingId={b.id} childOptions={b.child_ids || []} onSaved={load} />
                  </div>
                )}
                {showReview === b.id && (
                  <BookingReviewForm bookingId={b.id} sitterId={b.sitter_id} onDone={() => { setShowReview(null); load(); }} />
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
